import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip, useTheme } from "@mui/material";
import { Helmet } from "react-helmet";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArchiveIcon from "@mui/icons-material/Archive";
import CategoryIcon from "@mui/icons-material/Category";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { useLocation, useSearchParams } from "react-router-dom";

import SpecialPrayers from "./SpecialPrayers";
import SpecialPrayerCategoryList from "./SpecialPrayerCategory";
import Subscribers from "./Subscribers";
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import ModuleAnalyticsDashboard from "../components/ModuleAnalyticsDashboard";

const TABS = [
  { value: "prayers", label: "Special Prayers", icon: LocalFireDepartmentIcon },
  { value: "pending", label: "Pending Reviews", icon: PendingActionsIcon },
  { value: "unpublished", label: "Unpublished", icon: VisibilityOffIcon },
  { value: "archived", label: "Archived", icon: ArchiveIcon },
  { value: "categories", label: "Prayer Categories", icon: CategoryIcon },
  { value: "subscribers", label: "Subscribers", icon: PeopleAltIcon },
];

const PrayerCloset = ({ initialTab }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const getInitialTab = () => {
    const queryTab = searchParams.get("tab");
    if (queryTab && ["prayers", "pending", "unpublished", "archived", "categories", "subscribers", "analytics"].includes(queryTab)) {
      return queryTab;
    }
    const path = location.pathname;
    if (path.includes("pending")) return "pending";
    if (path.includes("unpublished")) return "unpublished";
    if (path.includes("archived")) return "archived";
    if (path.includes("special-prayer-categories")) return "categories";
    if (path.includes("subscribers")) return "subscribers";
    if (initialTab && initialTab !== "chain-prayer") return initialTab;
    return "prayers";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    setActiveTab(getInitialTab());
  }, [location.pathname, searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const getModuleConfig = () => {
    if (activeTab === "pending") {
      return { key: "special_prayers", title: "Pending Reviews" };
    }
    if (activeTab === "unpublished") {
      return { key: "special_prayers", title: "Unpublished Prayers" };
    }
    if (activeTab === "archived") {
      return { key: "special_prayers", title: "Archived Prayers" };
    }
    if (activeTab === "categories") {
      return { key: "special-prayer-categories", title: "Prayer Closet Categories" };
    }
    if (activeTab === "subscribers") {
      return { key: "special_prayers", title: "Prayer Closet Subscribers" };
    }
    return { key: "special_prayers", title: "Prayer Closet" };
  };

  const BORDER_COLOR = isLight ? "#CBD5E1" : "rgba(76, 161, 175, 0.45)";
  const PANEL_BG = isLight ? "#FFFFFF" : "#182232";

  return (
    <Box sx={{ width: "100%", pb: 4, pt: 1 }}>
      <Helmet><title>Prayer Closet - GPN</title></Helmet>

      {/* ── Top Bar: Module Title + Tabs on left, Settings icon at far right ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: { xs: "nowrap", md: "nowrap" },
          position: "relative",
          zIndex: 2,
          mb: "-1.5px",
          px: { xs: 0.5, sm: 1 },
          maxWidth: "100%",
        }}
      >
        {/* LEFT: Module Title + Tabs grouped together */}
        <Box sx={{
          display: "flex",
          alignItems: "flex-end",
          maxWidth: { xs: "calc(100% - 46px)", sm: "calc(100% - 50px)", md: "100%" },
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { height: "3px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: isLight ? "#CBD5E1" : "rgba(76, 161, 175, 0.4)", borderRadius: "3px" }
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.8, sm: 1.2 },
            pb: 1,
            px: { xs: 0.5, sm: 1 },
            mr: { xs: 1.5, sm: 2.5, md: 3.5 },
            userSelect: "none",
            cursor: "default",
            flexShrink: 0,
            borderBottom: isLight ? "2.5px solid #0D9488" : "2.5px solid rgba(76, 161, 175, 0.65)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: 30, sm: 36 },
              height: { xs: 30, sm: 36 },
              borderRadius: "8px",
              bgcolor: isLight ? "rgba(13, 148, 136, 0.15)" : "rgba(76, 161, 175, 0.18)",
              border: isLight ? "2px solid #0D9488" : "1.5px solid rgba(76, 161, 175, 0.5)",
              color: isLight ? "#0D9488" : "#64d2e2",
              flexShrink: 0,
            }}
          >
            <LocalFireDepartmentIcon sx={{ fontSize: { xs: 17, sm: 20 } }} />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{
              color: isLight ? "#0F172A" : "#e0f2fe",
              letterSpacing: "0.6px",
              fontSize: { xs: "1rem", sm: "1.15rem", md: "1.35rem" },
              lineHeight: 1.15,
              textTransform: "uppercase",
              fontFamily: "'Inter', sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            Prayer Closet
          </Typography>
        </Box>

        {/* Vertical Divider separating Title from Tab buttons */}
        <Box
          sx={{
            width: "2px",
            height: 26,
            bgcolor: isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.15)",
            mr: { xs: 1, sm: 2, md: 2.5 },
            mb: 0.8,
            flexShrink: 0,
          }}
        />

        {/* Tabs */}
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, flexShrink: 0 }}>
          {TABS.map(({ value, label, icon: Icon }) => {
            const isActive = activeTab === value;
            return (
              <Box
                key={value}
                onClick={() => handleTabChange(value)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.6, sm: 1 },
                  px: { xs: 1.4, sm: 2, md: 2.5 },
                  py: { xs: 0.8, sm: 1, md: 1.2 },
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  fontSize: { xs: "0.82rem", sm: "0.88rem", md: "0.95rem" },
                  fontWeight: isActive ? 800 : 600,
                  color: isActive ? (isLight ? "#0D9488" : "#FFFFFF") : (isLight ? "#0F172A" : "rgba(255, 255, 255, 0.75)"),
                  transition: "all 0.2s ease",
                  ...(isActive
                    ? {
                      backgroundColor: PANEL_BG,
                      borderTop: `2px solid ${isLight ? '#0D9488' : BORDER_COLOR}`,
                      borderLeft: `1.5px solid ${BORDER_COLOR}`,
                      borderRight: `1.5px solid ${BORDER_COLOR}`,
                      borderRadius: "8px 8px 0 0",
                      position: "relative",
                      zIndex: 10,
                      boxShadow: isLight ? "0 -2px 8px rgba(15, 23, 42, 0.08)" : "none",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: "-2px",
                        height: "4px",
                        backgroundColor: PANEL_BG,
                        zIndex: 12,
                      },
                    }
                    : {
                      backgroundColor: "transparent",
                      border: "none",
                      "&:hover": {
                        color: isLight ? "#0D9488" : "#FFFFFF",
                        backgroundColor: isLight ? "#F1F5F9" : "rgba(255,255,255,0.04)",
                        borderRadius: "8px 8px 0 0",
                      },
                    }),
                }}
              >
                <Icon sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, color: isActive ? (isLight ? "#0D9488" : "#4CA1AF") : (isLight ? "#0F766E" : "rgba(255, 255, 255, 0.7)") }} />
                <span>{label}</span>
              </Box>
            );
          })}
          </Box>
        </Box>

        {/* RIGHT: Analytics and Settings Icon Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "6px", mr: 0.5, zIndex: 10 }}>
          {/* Prayer Closet Analytics Icon Button */}
          <Tooltip title="Prayer Closet Analytics" placement="bottom">
            <IconButton
              onClick={() => handleTabChange("analytics")}
              sx={{
                color: activeTab === "analytics" ? "#FFFFFF" : (isLight ? "#0D9488" : "#64d2e2"),
                bgcolor: activeTab === "analytics"
                  ? (isLight ? "#0D9488" : "#177373")
                  : (isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)"),
                border: activeTab === "analytics"
                  ? "1.5px solid #64d2e2"
                  : (isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)"),
                borderRadius: "8px",
                p: 0.85,
                transition: "all 0.2s ease-in-out",
                boxShadow: activeTab === "analytics" ? "0 0 12px rgba(100, 210, 226, 0.4)" : "none",
                "&:hover": {
                  bgcolor: activeTab === "analytics"
                    ? (isLight ? "#0f766e" : "#146262")
                    : (isLight ? "rgba(13, 148, 136, 0.2)" : "rgba(76, 161, 175, 0.3)"),
                  transform: "translateY(-1px)",
                },
              }}
            >
              <AnalyticsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Settings Icon Button */}
          <Tooltip title={`${getModuleConfig().title} Settings`} placement="bottom">
            <IconButton
              onClick={() => setSettingsOpen(true)}
              sx={{
                color: isLight ? "#0D9488" : "#64d2e2",
                bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)",
                border: isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)",
                borderRadius: "8px",
                p: 0.85,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: isLight ? "rgba(13, 148, 136, 0.18)" : "rgba(76, 161, 175, 0.3)",
                  borderColor: isLight ? "#0D9488" : "#4CA1AF",
                  transform: "rotate(30deg)",
                },
              }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Main Content Box: Connected directly under the active tab ── */}
      <Box
        sx={{
          width: "100%",
          border: `1.5px solid ${BORDER_COLOR}`,
          borderRadius: "0 10px 10px 10px",
          backgroundColor: PANEL_BG,
          backdropFilter: "blur(20px)",
          boxShadow: isLight ? "0 4px 20px rgba(15, 23, 42, 0.08)" : "0 10px 30px rgba(0, 0, 0, 0.35)",
          p: { xs: 2, sm: 2.8 },
          position: "relative",
          zIndex: 1,
          minHeight: "75vh",
        }}
      >
        {activeTab === "prayers" && <SpecialPrayers hideHeader={true} onNavigateTab={handleTabChange} initialFilterTab="active" />}
        {activeTab === "pending" && <SpecialPrayers hideHeader={true} onNavigateTab={handleTabChange} initialFilterTab="pending" isPendingMode={true} />}
        {activeTab === "unpublished" && <SpecialPrayers hideHeader={true} onNavigateTab={handleTabChange} initialFilterTab="unpublished" isUnpublishedMode={true} />}
        {activeTab === "archived" && <SpecialPrayers hideHeader={true} onNavigateTab={handleTabChange} initialFilterTab="archived" isArchivedMode={true} />}
        {activeTab === "categories" && <SpecialPrayerCategoryList hideHeader={true} onNavigateTab={handleTabChange} />}
        {activeTab === "subscribers" && <Subscribers hideHeader={true} />}
        {activeTab === "analytics" && (
          <ModuleAnalyticsDashboard
            contentType="special_prayer"
            moduleTitle="Prayer Closet Activity & Warrior Analytics"
            hideHeader={true}
          />
        )}
      </Box>

      <ModuleSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        moduleKey={getModuleConfig().key}
        moduleTitle={getModuleConfig().title}
      />
    </Box>
  );
};

export default PrayerCloset;
